import { ISubscriptionPaymentSummary, SubscriptionType } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import {
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { getPerIntervalText, styles as SubscriptionSummaryStyles } from './SubscriptionSummary';

interface IProps {
  t: (key: string, options?: any) => string;
  paymentSubscriptionSummary: ISubscriptionPaymentSummary;
  showOrderSummaryNote: boolean;
}

export const getPerIntervalProRatedBilling = (t, interval) => {
  if (interval === 'week') {
    return t('weekly');
  }
  if (interval === 'year') {
    return t('yearly');
  }
  if (interval === 'day') {
    return t('daily');
  }

  return t('monthly');
};

export const PaymentSummaryDisplay: FunctionComponent<IProps> = ({
                                                                   t,
                                                                   paymentSubscriptionSummary,
                                                                   showOrderSummaryNote,
                                                                 }) => {
  const isTrialPeriodActive = paymentSubscriptionSummary.isTrialPeriodActive;

  const shouldNoteBeDisabledIfNotPaymentOrMetaData = showOrderSummaryNote &&
    paymentSubscriptionSummary.totalIncludingOnceOffPrices === 0 &&
    !paymentSubscriptionSummary.orderSummaryNote;

  if (shouldNoteBeDisabledIfNotPaymentOrMetaData) {
    showOrderSummaryNote = false;
  }

  const orderSummaryNote = isTrialPeriodActive && paymentSubscriptionSummary.orderSummaryNote;
  const planDescription = isTrialPeriodActive ?
    paymentSubscriptionSummary.planTrialDescription : paymentSubscriptionSummary.name;
  const interval = isTrialPeriodActive ? '' : getPerIntervalText(paymentSubscriptionSummary);

  return (
    <View style={styles.container}>
      <View style={[styles.row, styles.thinBorder]}>
        <View style={styles.inline}>
          <Text style={styles.subHeading}>{t('plan')}: </Text>
          <Text style={styles.valueText}>{planDescription}</Text>
        </View>
        <View style={[styles.alignEnd]}>
          <Text style={[styles.valueText, styles.bold]}>
            {paymentSubscriptionSummary.currencySymbol}{paymentSubscriptionSummary.amount}
          </Text>
          <Text style={SubscriptionSummaryStyles.lightText}>{interval}</Text>
        </View>
      </View>
      {paymentSubscriptionSummary.coupon && paymentSubscriptionSummary.coupon.valid && !isTrialPeriodActive &&
        <View style={[styles.row, styles.thinBorder]}>
          <View style={styles.inline}>
            <Text style={styles.subHeading}>{t('discount')}: </Text>
            <Text style={styles.valueText}>{paymentSubscriptionSummary.coupon.name}</Text>
          </View>
          {paymentSubscriptionSummary.coupon.percent_off &&
            <View style={styles.alignEnd}>
              <Text
                style={[styles.valueText, styles.bold]}>
                -{paymentSubscriptionSummary.coupon.percent_off.toFixed(0)}%
              </Text>
              <Text style={SubscriptionSummaryStyles.lightText}>{t('off')}</Text>
            </View>
          }
          {paymentSubscriptionSummary.coupon.amount_off &&
            <View style={styles.alignEnd}>
              <Text
                style={[styles.valueText, styles.bold]}>
                {paymentSubscriptionSummary.currencySymbol}{paymentSubscriptionSummary.coupon.amount_off / 100}
              </Text>
              <Text style={SubscriptionSummaryStyles.lightText}>{t('off')}</Text>
            </View>
          }
        </View>
      }
      <View style={[styles.thinBorder, styles.thinBorderBottom]}>
        <View style={styles.row}>
          <View style={[styles.inline, { flex: 1 }]}>
            <Text style={styles.totalHeading}>
              {t('total')}
            </Text>
          </View>
          <View style={[styles.alignEnd]}>
            <Text style={styles.totalHeading}>
              {paymentSubscriptionSummary.subscriptionDisplayTotalAfterDiscounts}
            </Text>
            <Text style={SubscriptionSummaryStyles.lightText}>{interval}</Text>
          </View>
        </View>
        {paymentSubscriptionSummary.subscriptionType !== SubscriptionType.PayAsYouGym && showOrderSummaryNote &&
          <View style={[styles.row, styles.prorataContainer]}>
            <Text style={styles.prorataText}>{orderSummaryNote}</Text>
          </View>
        }
      </View>
    </View>
  );
};

export const styles = StyleSheet.create({
  blackTextTheme: {
    color: Theme.GREY_DARK,
  },
  whiteTextTheme: {
    color: Theme.WHITE,
  },
  row: {
    flexDirection: 'row',
    paddingVertical: defaultScale(15),
    paddingHorizontal: 0,
  },
  inline: {
    flexDirection: 'row',
  },
  thinBorder: {
    borderTopColor: Theme.GREY_LIGHT,
    borderTopWidth: 1,
  },
  thinBorderBottom: {
    borderBottomColor: Theme.GREY_LIGHT,
    borderBottomWidth: 1,
  },
  alignEnd: {
    flex: 1,
    alignItems: 'flex-end',
  },
  subHeading: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.CHARCOAL,
  },
  totalHeading: {
    ...Theme.textStyle.heading,
  },
  bold: {
    ...Theme.textStyle.bodyCopyBold,
  },
  valueText: {
    ...Theme.textStyle.bodyCopyBold,
  },
  prorataContainer: {
    paddingTop: 0,
  },
  prorataText: {
    fontStyle: 'italic',
    color: Theme.GREY_DARK,
    fontFamily: Theme.CustomNormalItalic,
  },
  container: {
    paddingTop: defaultScale(20),
  },
});
